import { useCallback, useEffect, useRef, useState } from 'react';

import { getAssetPath } from '@/constants/configs';

import {
  getPreloadedScanner,
  getPreloadPromise,
} from '@/hooks/usePreloadOpenCV';

import type {
  CameraStatus,
  CornerPoints,
  DocumentScannerProps,
  FrameState,
  GuideRect,
  ScannerStep,
} from './documentScanner.types';
import {
  CORNER_KEYS,
  cornersClose,
  drawGuideFrame,
  getGuideRect,
  getResponsiveGuidePadding,
  isValidDocument,
  smoothCornerPoints,
} from './documentScanner.utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const cv: any;

/**
 * Dynamically injects a <script> tag and resolves when it has loaded.
 * If the script was already injected, resolves immediately.
 */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

const SMOOTH_ALPHA = 0.3;

const ERROR_TO_STATUS: Record<string, CameraStatus> = {
  NotAllowedError: 'denied',
  PermissionDeniedError: 'denied',
  NotFoundError: 'unavailable',
  DevicesNotFoundError: 'unavailable',
};

function offsetCorners(
  crop: CornerPoints,
  dx: number,
  dy: number,
): CornerPoints {
  const result = {} as CornerPoints;
  for (const k of CORNER_KEYS) {
    result[k] = { x: crop[k].x + dx, y: crop[k].y + dy };
  }
  return result;
}

interface UseScannerReturn {
  step: ScannerStep;
  cameraStatus: CameraStatus;
  capturedImageUrl: string | null;
  isSubmitting: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  overlayRef: React.RefObject<HTMLCanvasElement | null>;
  captureNow: () => void;
  retake: () => void;
  confirm: () => Promise<void>;
}

export function useDocumentScanner({
  autoCapture = true,
  holdTime = 3,
  guidePadding = 0.15,
  guideLabel,
  onConfirm,
}: DocumentScannerProps): UseScannerReturn {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);

  const [step, setStep] = useState<ScannerStep>('capture');
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>('pending');
  const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scannerRef = useRef<any>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animIdRef = useRef<number | null>(null);
  const capturedRef = useRef(false);
  const stableStartRef = useRef<number | null>(null);
  const prevCornersRef = useRef<CornerPoints | null>(null);
  const smoothCornersRef = useRef<CornerPoints | null>(null);
  const stableThresholdRef = useRef(80);
  const cameraReadyRef = useRef(false);
  const libsReadyRef = useRef(false);

  // Refs so the RAF scan loop always sees up-to-date values
  const holdTimeRef = useRef(holdTime);
  holdTimeRef.current = holdTime;
  const guidePaddingRef = useRef(guidePadding);
  guidePaddingRef.current = guidePadding;
  const activeGuidePaddingRef = useRef(guidePadding);
  const guideLabelRef = useRef(guideLabel);
  guideLabelRef.current = guideLabel;
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;
  const autoCaptureRef = useRef(autoCapture);
  autoCaptureRef.current = autoCapture;

  // ── Reset stability ──────────────────────────────────────

  const resetStability = useCallback(() => {
    prevCornersRef.current = null;
    smoothCornersRef.current = null;
    stableStartRef.current = null;
  }, []);

  // ── Capture logic ────────────────────────────────────────

  const doCapture = useCallback((sourceCanvas: HTMLCanvasElement) => {
    capturedRef.current = true;
    if (animIdRef.current !== null) {
      cancelAnimationFrame(animIdRef.current);
      animIdRef.current = null;
    }

    const vw = sourceCanvas.width;
    const vh = sourceCanvas.height;
    if (vw === 0 || vh === 0) return;

    const padding = activeGuidePaddingRef.current;
    const guide = getGuideRect(vw, vh, padding);
    const gw = Math.round(guide.w);
    const gh = Math.round(guide.h);

    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = gw;
    cropCanvas.height = gh;
    cropCanvas
      .getContext('2d')
      ?.drawImage(
        sourceCanvas,
        guide.x,
        guide.y,
        guide.w,
        guide.h,
        0,
        0,
        gw,
        gh,
      );

    const dataUrl = cropCanvas.toDataURL('image/jpeg', 0.92);
    setCapturedImageUrl(dataUrl);
    setStep('review');
  }, []);

  // ── Contour processing ──────────────────────────────────────

  const processContour = useCallback(
    (params: {
      vw: number;
      vh: number;
      padding: number;
      holdTime: number;
      guide: GuideRect;
      source: HTMLCanvasElement;
      ctx: CanvasRenderingContext2D;
      overlayW: number;
      overlayH: number;
    }): { frameState: FrameState; progress: number; captured: boolean } => {
      const {
        vw,
        vh,
        padding,
        holdTime: ht,
        guide,
        source: tmp,
        ctx,
        overlayW: cw,
        overlayH: ch,
      } = params;
      const cropped = document.createElement('canvas');
      cropped.width = Math.round(guide.w);
      cropped.height = Math.round(guide.h);
      cropped
        .getContext('2d')
        ?.drawImage(
          tmp,
          guide.x,
          guide.y,
          guide.w,
          guide.h,
          0,
          0,
          cropped.width,
          cropped.height,
        );

      let frameState: FrameState = 'idle';
      let progress = 0;
      let didCapture = false;

      try {
        const mat = cv.imread(cropped);
        const contour = scannerRef.current.findPaperContour(mat);
        mat.delete();

        if (!contour) {
          resetStability();
          return { frameState, progress, captured: false };
        }

        const cropCorners = scannerRef.current.getCornerPoints(
          contour,
        ) as CornerPoints;
        const rawCorners = offsetCorners(cropCorners, guide.x, guide.y);

        smoothCornersRef.current = smoothCornerPoints(
          rawCorners,
          smoothCornersRef.current,
          SMOOTH_ALPHA,
        );

        const validation = isValidDocument(
          smoothCornersRef.current,
          vw,
          vh,
          padding,
        );

        if (!validation.valid) {
          resetStability();
          return { frameState, progress, captured: false };
        }

        const isStable = cornersClose(
          smoothCornersRef.current,
          prevCornersRef.current,
          stableThresholdRef.current,
        );

        if (isStable) {
          if (!stableStartRef.current) stableStartRef.current = Date.now();
          const elapsed = (Date.now() - stableStartRef.current) / 1000;
          progress = Math.min(elapsed / ht, 1);
          frameState = 'locking';

          if (progress >= 1) {
            drawGuideFrame(ctx, cw, ch, 'locking', 1, padding);
            doCapture(tmp);
            didCapture = true;
          }
        } else {
          stableStartRef.current = null;
          frameState = 'detected';
        }
        prevCornersRef.current = smoothCornersRef.current;
      } catch {
        // contour detection can throw — keep scanning
      }

      return { frameState, progress, captured: didCapture };
    },
    [doCapture, resetStability],
  );

  // ── Scan loop (uses refs only — stable identity) ─────────

  const scanLoop = useCallback(() => {
    if (capturedRef.current) return;

    const video = videoRef.current;
    const overlay = overlayRef.current;
    if (!video || !overlay) return;

    const ctx = overlay.getContext('2d');
    if (!ctx) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (vw === 0 || vh === 0) {
      animIdRef.current = requestAnimationFrame(scanLoop);
      return;
    }

    // Match overlay resolution to its CSS size (× dpr) to avoid stretch
    // from mapping a mismatched coordinate space onto the container.
    const dpr = window.devicePixelRatio || 1;
    const displayW = Math.round(overlay.clientWidth * dpr);
    const displayH = Math.round(overlay.clientHeight * dpr);
    if (overlay.width !== displayW || overlay.height !== displayH) {
      overlay.width = displayW;
      overlay.height = displayH;
    }

    const cw = overlay.width;
    const ch = overlay.height;
    ctx.clearRect(0, 0, cw, ch);

    const padding = getResponsiveGuidePadding(
      overlay.clientWidth,
      overlay.clientHeight,
      guidePaddingRef.current,
    );
    activeGuidePaddingRef.current = padding;
    const ht = holdTimeRef.current;

    let frameState: FrameState = 'idle';
    let progress = 0;

    if (autoCaptureRef.current && libsReadyRef.current && scannerRef.current) {
      const tmp = document.createElement('canvas');
      tmp.width = vw;
      tmp.height = vh;
      tmp.getContext('2d')?.drawImage(video, 0, 0);

      const guide = getGuideRect(vw, vh, padding);
      const result = processContour({
        vw,
        vh,
        padding,
        holdTime: ht,
        guide,
        source: tmp,
        ctx,
        overlayW: cw,
        overlayH: ch,
      });
      frameState = result.frameState;
      progress = result.progress;
      if (result.captured) return;
    }

    drawGuideFrame(
      ctx,
      cw,
      ch,
      frameState,
      progress,
      padding,
      guideLabelRef.current,
    );
    animIdRef.current = requestAnimationFrame(scanLoop);
  }, [processContour]);

  // ── Start scan loop (called when camera becomes ready) ──

  const tryStartScanLoop = useCallback(() => {
    if (!cameraReadyRef.current) return;
    if (capturedRef.current) return;
    if (animIdRef.current !== null) return; // already running
    scanLoop();
  }, [scanLoop]);

  // ── Check camera availability ────────────────────────────

  const checkCameraAvailability = async (): Promise<boolean> => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some(d => d.kind === 'videoinput');
    } catch {
      return false;
    }
  };

  // ── Initialize: start camera immediately, load libs in parallel ──

  useEffect(() => {
    let cancelled = false;

    const startCamera = async () => {
      const hasCamera = await checkCameraAvailability();
      if (cancelled) return;
      if (!hasCamera) {
        setCameraStatus('unavailable');
        return;
      }

      setCameraStatus('requesting');

      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        if (cancelled) {
          mediaStream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = mediaStream;

        const video = videoRef.current;
        if (!video) return;

        video.srcObject = mediaStream;
        await video.play();

        const overlay = overlayRef.current;
        if (overlay) {
          const dpr = window.devicePixelRatio || 1;
          overlay.width = Math.round(overlay.clientWidth * dpr);
          overlay.height = Math.round(overlay.clientHeight * dpr);
        }

        const diag = Math.hypot(video.videoWidth, video.videoHeight);
        stableThresholdRef.current = diag * 0.12;

        cameraReadyRef.current = true;
        capturedRef.current = false;
        smoothCornersRef.current = null;

        if (!autoCaptureRef.current || libsReadyRef.current) {
          setCameraStatus('active');
          tryStartScanLoop();
        } else {
          setCameraStatus('loading-libs');
        }
      } catch (err) {
        if (cancelled) return;
        const error = err as DOMException;
        setCameraStatus(ERROR_TO_STATUS[error.name] ?? 'denied');
        console.error('Camera access failed:', err);
      }
    };

    // OpenCV is copied to public/assets/js/ via postinstall
    const waitForLibs = async () => {
      // If usePreloadOpenCV already finished, reuse its cached instance.
      const preloaded = getPreloadedScanner();
      if (preloaded) {
        scannerRef.current = preloaded;
        libsReadyRef.current = true;
        if (cameraReadyRef.current) {
          setCameraStatus('active');
          tryStartScanLoop();
        }
        return;
      }

      // If preloading is already in flight, piggy-back on it instead of
      // starting a second download.
      const existingPromise = getPreloadPromise();
      if (existingPromise) {
        await existingPromise;
        if (cancelled) return;
        const scanner = getPreloadedScanner();
        if (scanner) {
          scannerRef.current = scanner;
          libsReadyRef.current = true;
          if (cameraReadyRef.current) {
            setCameraStatus('active');
            tryStartScanLoop();
          }
          return;
        }
        // preload failed — fall through to manual loading below
      }

      try {
        await loadScript(getAssetPath('assets/js/opencv.js'));
      } catch (e) {
        console.error('Failed to load OpenCV:', e);
        return;
      }
      if (cancelled) return;

      // cv.then() resolves when the WASM runtime is ready, or immediately
      // if onRuntimeInitialized has already fired.
      if (cv !== undefined && typeof cv.then === 'function') {
        await new Promise<void>(resolve => {
          cv.then(() => resolve());
        });
      }
      if (cancelled) return;

      const jscanifyModule = await import('jscanify/client');
      const JscanifyClass = jscanifyModule.default || jscanifyModule;
      if (cancelled) return;
      scannerRef.current = new JscanifyClass();
      libsReadyRef.current = true;

      if (cameraReadyRef.current) {
        setCameraStatus('active');
        tryStartScanLoop();
      }
    };

    startCamera();
    if (autoCaptureRef.current) waitForLibs();

    return () => {
      cancelled = true;
      if (animIdRef.current !== null) {
        cancelAnimationFrame(animIdRef.current);
        animIdRef.current = null;
      }
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Re-attach stream after retake ──────────────────────
  useEffect(() => {
    if (step !== 'capture') return;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;
    // Only reassign when the video lost its srcObject (remount)
    if (video.srcObject !== stream) {
      video.srcObject = stream;
      video.play().catch(() => {});
    }
    tryStartScanLoop();
  }, [step, tryStartScanLoop]);

  // ── Public actions ───────────────────────────────────────

  const captureNow = useCallback(() => {
    if (capturedRef.current) return;
    const video = videoRef.current;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) return;

    const tmp = document.createElement('canvas');
    tmp.width = video.videoWidth;
    tmp.height = video.videoHeight;
    tmp.getContext('2d')?.drawImage(video, 0, 0);
    doCapture(tmp);
  }, [doCapture]);

  const retake = useCallback(() => {
    setCapturedImageUrl(null);
    setStep('capture');
    capturedRef.current = false;
    resetStability();

    // If the camera stream is still alive (common after hot reload),
    // immediately mark the camera as active so the StatusOverlay doesn't
    // block the live feed.
    const hasLiveStream = streamRef.current
      ?.getTracks()
      .some(t => t.readyState === 'live');
    if (hasLiveStream) {
      setCameraStatus('active');
    }

    tryStartScanLoop();
  }, [resetStability, tryStartScanLoop]);

  const confirm = useCallback(async () => {
    if (capturedImageUrl && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await onConfirmRef.current(capturedImageUrl);
      } catch (error) {
        console.error('Document confirmation failed:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [capturedImageUrl, isSubmitting]);

  return {
    step,
    cameraStatus,
    capturedImageUrl,
    isSubmitting,
    videoRef,
    overlayRef,
    captureNow,
    retake,
    confirm,
  };
}
